// A clock shared by the tiles of a video wall: server time estimated from a few round trips to
// GET Screen/time, kept on a monotonic local clock so device clock jumps do no harm.

const SLEW_MS = 2000; // corrections smaller than STEP_MS are eased in over this long
const STEP_MS = 1000; // anything bigger is applied at once

// Milliseconds since the epoch on a clock that never jumps (performance.now from page load).
const timeOrigin =
  typeof performance !== 'undefined' && performance.timeOrigin
    ? performance.timeOrigin
    : Date.now() - (typeof performance !== 'undefined' ? performance.now() : 0);

export const monotonicNow = () =>
  typeof performance !== 'undefined'
    ? timeOrigin + performance.now()
    : Date.now();

/**
 * Server minus local time from round-trip samples `{ sentAt, receivedAt, serverMs }` (local
 * times on the same clock). The fastest round trip has the least room for asymmetric delay, so
 * it wins; the server is assumed to have answered half-way through it. Null without samples.
 */
export const bestOffset = (samples) => {
  const valid = (samples || []).filter(
    (s) => Number.isFinite(s?.serverMs) && s.receivedAt >= s.sentAt,
  );
  if (!valid.length) return null;
  const best = valid.reduce((a, b) =>
    b.receivedAt - b.sentAt < a.receivedAt - a.sentAt ? b : a,
  );
  return best.serverMs - (best.sentAt + best.receivedAt) / 2;
};

/** The offset to use at `at` while easing from `from` to `to` (started at `since`). */
export const slewedOffset = (from, to, since, at) => {
  if (from == null || Math.abs(to - from) >= STEP_MS) return to;
  const progress = Math.min(Math.max((at - since) / SLEW_MS, 0), 1);
  return from + (to - from) * progress;
};

/**
 * `now()` is the synced time in epoch milliseconds. `sync()` measures `samples` round trips with
 * `fetchServerMs` and adopts the result; until the first success `seedOffsetMs` (e.g. from the
 * schedule bundle) is used. A failed sync (offline) keeps the last offset.
 */
export const createClockSync = ({
  fetchServerMs,
  seedOffsetMs = 0,
  samples = 5,
  gapMs = 150,
  localNow = monotonicNow,
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
}) => {
  let from = null;
  let to = seedOffsetMs;
  let since = 0;
  let synced = false;

  const offset = (at = localNow()) => slewedOffset(from, to, since, at);

  return {
    now: () => {
      const at = localNow();
      return at + offset(at);
    },
    offset,
    isSynced: () => synced,
    sync: async () => {
      const taken = [];
      for (let i = 0; i < samples; i++) {
        if (i > 0) await wait(gapMs);
        try {
          const sentAt = localNow();
          const serverMs = await fetchServerMs();
          taken.push({ sentAt, receivedAt: localNow(), serverMs });
        } catch {
          // A lost sample; the others still count.
        }
      }
      const measured = bestOffset(taken);
      if (measured == null) return false;
      const at = localNow();
      from = synced ? offset(at) : null; // the first sync replaces the rough seed outright
      to = measured;
      since = at;
      synced = true;
      return true;
    },
  };
};
